'use client'

import { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import { usePathname, useSearchParams } from 'next/navigation'
import { GrowwConnector } from '../GrowwConnector'

interface GrowwGTTOrderItem {
  id: string
  symbol: string
  exchange: string
  triggerType: string
  triggerPrice: number
  limitPrice?: number
  quantity: number
  side: string
  status: string
  createdAt: number
  expiresAt?: number
  product?: string
}

// Common stock symbols for the dropdown
const COMMON_SYMBOLS = [
  'ALL', // Show all GTT orders
  'RELIANCE',
  'TCS',
  'HDFCBANK',
  'INFY',
  'ICICIBANK',
  'HINDUNILVR',
  'SBIN',
  'BAJFINANCE',
  'BHARTIARTL',
  'KOTAKBANK',
  'WIPRO',
  'AXISBANK',
  'TATAMOTORS',
  'ADANIENT',
  'NIFTY 50'
]

export default function GTTOrderHistory({ apiKey, accessToken }: { apiKey: string, accessToken?: string }) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const pathSymbol = pathname ? pathname.split('/').pop()?.toUpperCase() : ''

  const urlSymbol = pathSymbol || searchParams?.get('symbol')

  const [gttOrders, setGTTOrders] = useState<GrowwGTTOrderItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSymbol, setSelectedSymbol] = useState('ALL')
  const [customSymbol, setCustomSymbol] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)

  useEffect(() => {
    fetchGTTOrderHistory(selectedSymbol)
  }, [selectedSymbol])

  const fetchGTTOrderHistory = async (symbol: string) => {
    setIsLoading(true)
    setError(null)

    try {
      if (!apiKey || !accessToken) {
        throw new Error('API Key and Access Token are required')
      }

      // Fetch GTT orders from the API endpoint
      const response = await fetch('/api/connectors/Groww/gtt/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey,
          accessToken,
          symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch GTT orders')
      }

      const ordersData = await response.json()

      // Sort orders by timestamp, most recent first
      const sortedOrders = [...ordersData].sort((a, b) => b.createdAt - a.createdAt)
      setGTTOrders(sortedOrders)
    } catch (err: any) {
      console.error('Error fetching GTT order history:', err)
      setError(
        err.message ||
          'Failed to fetch GTT order history. Please ensure you are connected to Groww.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSymbolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === 'custom') {
      setShowCustomInput(true)
    } else {
      setShowCustomInput(false)
      setSelectedSymbol(value)
    }
  }

  const handleCustomSymbolSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (customSymbol) {
      setSelectedSymbol(customSymbol.toUpperCase())
      setCustomSymbol('')
      setShowCustomInput(false)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString()
  }

  if (isLoading)
    return <div className={styles.loadingState}>Loading GTT orders...</div>
  if (error) return <div className={styles.errorState}>{error}</div>

  return (
    <div className={styles.ordersContainer}>
      <div className={styles.symbolSelector}>
        <div className={styles.selectorWrapper}>
          <select
            value={
              showCustomInput
                ? 'custom'
                : COMMON_SYMBOLS.includes(selectedSymbol)
                  ? selectedSymbol
                  : 'custom'
            }
            onChange={handleSymbolChange}
            className={styles.symbolDropdown}
          >
            {COMMON_SYMBOLS.map(symbol => (
              <option key={symbol} value={symbol}>
                {symbol}
              </option>
            ))}
            <option value="custom">Custom Symbol</option>
          </select>

          {(showCustomInput || !COMMON_SYMBOLS.includes(selectedSymbol)) && (
            <form
              onSubmit={handleCustomSymbolSubmit}
              className={styles.customSymbolForm}
            >
              <input
                type="text"
                value={
                  customSymbol ||
                  (!COMMON_SYMBOLS.includes(selectedSymbol) && !showCustomInput
                    ? selectedSymbol
                    : '')
                }
                onChange={e => setCustomSymbol(e.target.value)}
                placeholder="Enter symbol (e.g. RELIANCE)"
                className={styles.customSymbolInput}
                autoFocus
              />
              <button type="submit" className={styles.submitButton}>
                Go
              </button>
            </form>
          )}
        </div>
        <div className={styles.currentSymbol}>
          Current: <strong>{selectedSymbol}</strong>
        </div>
      </div>

      {gttOrders.length === 0 ? (
        <div className={styles.noOrders}>
          {selectedSymbol === 'ALL' 
            ? 'No GTT orders found' 
            : `No GTT orders found for ${selectedSymbol}`}
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Symbol</th>
                <th>Side</th>
                <th>Trigger Type</th>
                <th>Trigger Price</th>
                <th>Limit Price</th>
                <th>Quantity</th>
                <th>Status</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {gttOrders.map((order, index) => (
                <tr key={index}>
                  <td>{formatDate(order.createdAt)}</td>
                  <td>{order.symbol}</td>
                  <td
                    className={
                      order.side === 'BUY' ? styles.buyOrder : styles.sellOrder
                    }
                  >
                    {order.side}
                  </td>
                  <td>{order.triggerType}</td>
                  <td>₹{order.triggerPrice}</td>
                  <td>{order.limitPrice ? `₹${order.limitPrice}` : '-'}</td>
                  <td>{order.quantity}</td>
                  <td>{order.status}</td>
                  <td>{order.expiresAt ? formatDate(order.expiresAt) : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}